import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RESTORE_EVENT,
  RESTORE_SCOPES,
  SCOPE_LABELS,
  captureRestorePoint,
  listRestoreTimeline,
  restoreFromPoint,
  scopeLabel,
} from "../../js/accounts/restorePoints";
import { formatDate } from "../../js/utils/formatters";
import RestoreConfirmModal from "./RestoreConfirmModal";

const SOURCE_LABELS = {
  auto: "Auto-save",
  manual: "Manual",
  pre_change: "Before change",
};

export default function RestoreTimeline({
  session,
  userId,
  adminMode = false,
  title = "Restore timeline",
  lead = "Snapshots are saved as you work. Pick a point to roll back on this device.",
  onFlash,
  scopeFilter = "",
}) {
  const targetId = userId || session?.userId || "";
  const [scope, setScope] = useState(scopeFilter);
  const [tick, setTick] = useState(0);
  const [confirmPoint, setConfirmPoint] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const refresh = () => setTick((value) => value + 1);
    window.addEventListener(RESTORE_EVENT, refresh);
    return () => window.removeEventListener(RESTORE_EVENT, refresh);
  }, []);

  const timeline = useMemo(
    () => listRestoreTimeline(targetId, { scope, limit: adminMode ? 80 : 50 }),
    [targetId, scope, adminMode, tick]
  );

  function flash(message) {
    if (onFlash) onFlash(message);
  }

  function snapshotNow() {
    if (!targetId) return;
    const roleScope =
      session?.role === "buyer"
        ? RESTORE_SCOPES.buyer
        : session?.role === "seller"
          ? RESTORE_SCOPES.seller
          : session?.role === "broker"
            ? RESTORE_SCOPES.broker_desk
            : session?.role === "agent"
              ? RESTORE_SCOPES.agent_desk
              : RESTORE_SCOPES.settings;
    captureRestorePoint(targetId, {
      label: "Manual snapshot",
      actorId: session?.userId || "",
      scope: adminMode ? RESTORE_SCOPES.account : roleScope,
      source: "manual",
      force: true,
    });
    flash("Restore point saved.");
    setTick((value) => value + 1);
  }

  function handleConfirm(point) {
    setBusy(true);
    try {
      restoreFromPoint(session, point.id, { confirmed: true });
      flash("Restored. Reload the page if your desk does not refresh automatically.");
      setConfirmPoint(null);
      setTick((value) => value + 1);
    } catch (err) {
      flash(err.message || "Restore failed.");
    } finally {
      setBusy(false);
    }
  }

  const scopes = Object.keys(SCOPE_LABELS);

  return (
    <section className="card restore-timeline">
      <header className="restore-timeline__head">
        <div>
          <h2>{title}</h2>
          <p className="muted">{lead}</p>
        </div>
        <button type="button" className="ghost-btn" onClick={snapshotNow}>
          Snapshot now
        </button>
      </header>

      <div className="restore-filters">
        <label className="field-label" htmlFor="restore-scope-filter">
          Scope
        </label>
        <select
          id="restore-scope-filter"
          className="field-select"
          value={scope}
          onChange={(event) => setScope(event.target.value)}
        >
          <option value="">All scopes</option>
          {scopes.map((id) => (
            <option key={id} value={id}>
              {SCOPE_LABELS[id]}
            </option>
          ))}
        </select>
      </div>

      <ol className="restore-timeline__list">
        {timeline.length ? (
          timeline.map((row) => (
            <li key={row.id} className="restore-timeline__item">
              <div className="restore-timeline__meta">
                <time dateTime={row.at}>{formatDate(row.at)}</time>
                <span className="restore-badge">{scopeLabel(row.scope)}</span>
                <span className="restore-badge restore-badge--quiet">{SOURCE_LABELS[row.source] || row.source}</span>
              </div>
              <p className="restore-timeline__label">{row.label || "Snapshot"}</p>
              {adminMode && row.actorId ? <p className="muted">Actor: {row.actorId}</p> : null}
              {row.actionType ? <p className="muted">{row.actionType}</p> : null}
              <button type="button" className="text-btn" onClick={() => setConfirmPoint(row)}>
                Restore
              </button>
            </li>
          ))
        ) : (
          <li className="muted restore-timeline__empty">No restore points yet. Edits are saved automatically as you work.</li>
        )}
      </ol>

      <p className="muted restore-legal-note">
        Restore points are device-local and capped — not a cloud backup. See the{" "}
        <Link to="/eula">EULA</Link> and <Link to="/disclaimer">Disclaimer</Link>. For long-term retention,{" "}
        <Link to="/backup">export a backup</Link>.
      </p>

      <RestoreConfirmModal
        point={confirmPoint}
        isOpen={Boolean(confirmPoint)}
        onClose={() => setConfirmPoint(null)}
        onConfirm={handleConfirm}
        busy={busy}
      />
    </section>
  );
}
